import InfoDot from "@components/infoDot";
import RadioSelectSettingModal from "@components/radioSelectSettingModal";
import SettingsListItemIcon from "@components/settingsListItemIcon";
import { IonItem, IonLabel, IonNote } from "@ionic/react";
import { IDeck } from "@models/deck";
import {
  FSRSRetentionPreset,
  getFSRSRetentionValue,
  setDeckFSRSRetention,
} from "@models/deckSettings";
import { DotID, clearDot } from "dots";
import { useIonCardModalWithDismiss } from "hooks/util/useIonModalWithDismiss";
import L10n from "localization";
import { useCallback } from "react";
import { PiBrain } from "react-icons/pi";

interface IProps {
  deck: IDeck;
  currentPreset: FSRSRetentionPreset;
  onError: (error: Error) => void;
}

export default function FSRSRetentionSettingItem({
  deck,
  currentPreset,
  onError,
}: IProps): JSX.Element {
  const handlePresetChange = useCallback(
    async (preset: FSRSRetentionPreset) => {
      try {
        await setDeckFSRSRetention(deck, preset);
      } catch (error) {
        onError(error as Error);
      }
    },
    [deck, onError],
  );

  const options = [
    {
      name: L10n.localize((s) => s.fsrs.retentionLightName),
      val: "light" as FSRSRetentionPreset,
      desc: L10n.localize((s) => s.fsrs.retentionLightDescription),
    },
    {
      name: L10n.localize((s) => s.fsrs.retentionBalancedName),
      val: "balanced" as FSRSRetentionPreset,
      desc: L10n.localize((s) => s.fsrs.retentionBalancedDescription),
    },
    {
      name: L10n.localize((s) => s.fsrs.retentionIntensiveName),
      val: "aggressive" as FSRSRetentionPreset,
      desc: L10n.localize((s) => s.fsrs.retentionIntensiveDescription),
    },
  ];

  const dotID: DotID = "fsrs_retention_setting";
  const [showModal] = useIonCardModalWithDismiss(RadioSelectSettingModal, {
    options,
    title: L10n.localize((s) => s.fsrs.retentionTitle),
    onChange: (val: string) => handlePresetChange(val as FSRSRetentionPreset),
    selectedItemVal: currentPreset,
    description: L10n.localize((s) => s.fsrs.retentionDescription),
  });

  const handleClick = useCallback(() => {
    clearDot(dotID).catch(() => {});
    showModal();
  }, [showModal]);

  const currentRetention = getFSRSRetentionValue(currentPreset);
  const displayName = options.find((opt) => opt.val === currentPreset)?.name || "Balanced";

  return (
    <IonItem button onClick={handleClick}>
      <InfoDot id={dotID} />
      <SettingsListItemIcon icon={PiBrain} />
      <IonLabel>{L10n.localize((s) => s.fsrs.retentionTitle)}</IonLabel>
      <IonNote slot="end">
        <div style={{ textAlign: "right" }}>
          <div>{displayName.split(" ")[0]}</div>
          <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
            {Math.round(currentRetention * 100)}%
          </div>
        </div>
      </IonNote>
    </IonItem>
  );
}
